import _ from 'lodash';
import CacheableDataObject from './CacheableDataObject';
import moment from 'moment';
import TransactionSplit from './TransactionSplit';
import transactionSerializer from './services/transactionSerializer.jsx';
import TransactionRenderKey from '../utilities/TransactionRenderKey.jsx';

export default class Transaction extends CacheableDataObject {
	static instanceCount = 1;
	static categoryRetrieveCallback = null; //... Hacky workaround until this can properly get injected...?
	static transactionDateFormat = {
		sameDay: '[Today]',
		nextDay: '[Tomorrow]',
		nextWeek: 'dddd',
		lastDay: '[Yesterday]',
		lastWeek: 'MM/DD/YYYY',
		sameElse: 'MM/DD/YYYY'
	};

	static transactionSort(a, b) {
		if(!a.transaction_date && b.transaction_date) {
			return 1;
		}
		if(a.transaction_date && !b.transaction_date) {
			return -1;
		}
		if(!a.transaction_date && !b.transaction_date) {
			return Transaction.idCompare(a, b);
		}
		if(a.transaction_date.isSame(b.transaction_date, 'day')) {
			return Transaction.idCompare(a, b);
		}

		if(a.transaction_date.isAfter(b.transaction_date)) {
			return -1;
		} else {
			return 1;
		}
	}

	static idCompare(a, b) {
		if(a.id > 0 && b.id > 0) {
			return a.id > b.id ? -1 : 1;
		}
		if(a.id < 0 && b.id > 0) {
			return -1;
		}
		if(a.id > 0 && b.id < 0) {
			return 1;
		}
		if(a.id < 0 && b.id < 0) {
			return a.id > b.id ? -1 : 1;
		}
	}

	beforeConstruct(data) {
		this.instanceNumber = ++Transaction.instanceCount;
		this.deleted = false;
		this.deleteEdit = null;
		this.where = null;
		this.amount = null;
		this.transaction_date = null;
		/**
		 *
		 * @type {TransactionSplit[]}
		 */
		this.splits = [];
		this.hadSplit = false;
		this.typeof = 'Transaction';

		this.initializeData = {};
		if(data) {
			this.initializeData = data;
		}
	}

	getCategories() {
		const categories = [];
		if(this.isSplit()) {
			this.splits.forEach((split) => {
				categories.push(split.category_id);
			});
		} else {
			categories.push(this.category_id);
		}
		return categories;
	}

	getRenderKey() {
		return TransactionRenderKey.getKey(this, {
			[TransactionRenderKey.TYPE_PLAID]: true,
			[TransactionRenderKey.TYPE_TRANSACTION_ID]: true,
			[TransactionRenderKey.TYPE_SUBMIT_ID]: true
		});
	}

	isBankTransaction() {
		return false;
	}

	toggleAmountSign() {
		this.amount = this.amount * -1;
	}

	isSetToAnExpenseCategory() {
		if(Transaction.categoryRetrieveCallback) {
			return Transaction.categoryRetrieveCallback(this);
		}
		return null;
	}

	isAnExpenseBasedOnAmountAndCategory() {
		const isSetToAnExpenseCategory = this.isSetToAnExpenseCategory();
		if(isSetToAnExpenseCategory) {
			return this.amount >= 0;
		} else {
			return this.amount <= 0;
		}
	}

	resetToEmpty() {
		this.update({
			where: this.initializeData.where || '',
			amount: this.initializeData.amount || 0,
			category_id: this.initializeData.category_id || ''
		});
		this.resetSplits();
	}

	resetSplits() {
		this.splits = [];
	}

	clearEmptySplits() {
		this.splits = this.splits.filter((split) => {
			return !!split.id;
		});
	}

	removeSplit(removeSplit) {
		this.splits = this.splits.filter((split) => {
			return split !== removeSplit;
		});
	}

	addSplit() {
		if(this.splits.length === 0) {
			this._addSplitHelper();
			this._addSplitHelper();
		} else {
			this._addSplitHelper();
		}
	}

	splitsAreMissingACategory() {
		let missing = false;
		this.splits.forEach((split) => {
			if(!split.category_id) {
				missing = true;
			}
		});
		return missing;
	}

	_addSplitHelper() {
		const split = TransactionSplit.create();
		split.transaction = this;
		this.splits.push(split);
	}

	isSplit() {
		return this.splits.length > 0;
	}

	getUnsplitAmount() {
		let total = this.amount;
		this.splits.forEach((split) => {
			total -= split.amount;
		});
		return total;
	}

	toIndexString() {
		const date = this.transaction_date ?
			`${this.transaction_date.month()}-${this.transaction_date.year()}` :
			'unknown';
		return `${date}-${this.where}-${this.amount}-${this.instanceNumber}`;
	}

	getAmountTowardCategory(category) {
		if(this.splits.length === 0) {
			return this.amount;
		} else {
			let total = 0;
			for(let split of this.splits) {
				if(split.category_id === category.id) {
					total += split.amount;
				}
			}
			return total;
		}
	}

	isComplete() {
		return this.where !== null && this.amount !== null && this.transaction_date !== null;
	}

	serialize() {
		return transactionSerializer(this);
	}

	getPlaidTransaction() {
		if(this.plaid_transaction_id) {
			const transaction = window.transactionFinder.plaidTransactions.transactions.get(this.plaid_transaction_id);
			return transaction;
		}
		return null;
	}

	revert() {
		this.copy(this.beforeChanges);
	}

	clone() {
		const transaction = Transaction.create().copy(this);
		transaction.original = this;
		transaction.beforeChanges = Transaction.create().copy(this);
		return transaction;
	}

	copy(fromOther) {
		this.id = fromOther.id;
		this.where = fromOther.where;
		this.description = fromOther.description;
		this.transaction_date = fromOther.transaction_date?.clone();
		for(let split of fromOther.splits) {
			this.splits.push(split.clone());
		}
		this.category_id = fromOther.category_id;
		this.amount = fromOther.amount;
		this.plaid_transaction_id = fromOther.plaid_transaction_id;
		this.plaid_transaction = fromOther.plaid_transaction;
		this.submit_id = fromOther.submit_id;
		return this;
	}

	get year() {
		return this.transaction_date?.year();
	}

	get month() {
		return this.transaction_date?.month();
	}

	static getBlankTransactionCategoryDisplayText(transaction) {
		if(transaction.isSplit()) {
			return 'Split';
		}
		return undefined;
	}
}

Transaction.prototype.dataLocationParsers = {
	description: function(transaction, data) {
		transaction.description = data.description;
	},
	where: (transaction, data) => {
		transaction.where = data.where;
	},
	splits: (transaction, data) => {
		transaction.splits = [];
		_.each(data.splits, (split) => {
			transaction.splits.push(TransactionSplit.create(split));
		});
		transaction.hadSplit = true;
	},
	category_id: (transaction, data) => {
		const value = data.category_id === null || data.category_id === undefined ? null : Number.parseInt(data.category_id, 10);
		transaction.category_id = (value === undefined || value === null) ? null : value;
	},
	amount: (transaction, data) => {
		transaction.amount = Number.parseInt(data.amount, 10);
	},
	transaction_date: (transaction, data) => {
		if(data.transaction_date) {
			if(moment.isMoment(data.transaction_date)) {
				transaction.transaction_date = data.transaction_date.clone();
			} else {
				transaction.transaction_date = moment.unix(data.transaction_date);
			}
		} else {
			transaction.transaction_date = null;
		}
	},
};